<template>
  <with-loader id="variables-usages" class="p-14 w-full h-full" :loading="isLoading">
    <div class="text-gray-dark text-lg pb-5 border-solid border-b border-0 border-gray w-full" v-html="summaryText"/>

    <perfect-scrollbar class="pr-4 pb-4 variable-usages-list">
      <div v-for="itemType in ['answers', 'events', 'blocks']"
           :key="itemType"
      >
        <div v-if="!isEmpty(getRefItems(itemType.toLowerCase()))" class="py-3 mt-6 flex gap-1 items-baseline">
          <span class="text-lg font-bold ml-px capitalize text-gray-dark">{{itemType}}</span>
          <component :is="iconMap[itemType]" class="h-4"/>
        </div>

        <div class="flex flex-col gap-2">
          <div v-for="ref in getRefItems(itemType)"
               :key="ref.id"
               class="flex items-center justify-between gap-1"
          >
            <span class="text-base text-gray-dark truncate" :class="{ 'w-1/2': hasMultipleBots }">
              {{ref.name}}
            </span>

            <div class="flex items-center" :class="hasMultipleBots ? 'w-1/2 justify-between gap-1' : 'justify-end'">
              <span v-if="hasMultipleBots" class="text-base text-left text-gray truncate" :title="getBotName(linkBotId(ref))">
                {{ getBotName(linkBotId(ref)) }}
              </span>
              <base-button variant="bordered"
                           class="h-8 px-4 items-center"
                           :router-link="itemRoute(ref, itemType)"
                           @click.native="$emit('show-item')"
              >
                Show
              </base-button>
            </div>
          </div>
        </div>
      </div>
    </perfect-scrollbar>
  </with-loader>
</template>

<script>
import variablesService from '@/services/amio/variables.service'
import toaster from '@/utils/toaster'
import EventsIcon from '@/assets/images/icons/EventsIcon'
import AnswersIcon from '@/assets/images/icons/AnswersIcon'
import BlocksIcon from '@/assets/images/icons/BlocksIcon'
import isEmpty from 'ramda/src/isEmpty'
import WithLoader from '@/components/common/WithLoader.vue'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import BaseButton from '@/components/base-components/BaseButton.vue'
import botModule from '@/store/modules/bot.module'
import variablesModule from '@/store/modules/variables.module'

export default {
  name: 'VariablesUsages',

  components: {
    BaseButton,
    WithLoader,
    EventsIcon,
    AnswersIcon,
    BlocksIcon,
    PerfectScrollbar
  },

  props: {
    variableId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isLoading: false,
      answers: [],
      events: [],
      blocks: [],
      usageListHeight: 0
    }
  },

  computed: {
    botList: botModule.getBotList,
    variables: variablesModule.variables,

    hasMultipleBots() {
      return this.botList.length > 1
    },

    viewedVariable() {
      return this.variables.find(variable => variable.id === this.variableId)
    },

    variableName() {
      return this.viewedVariable.name
    },

    iconMap() {
      return {
        answers: AnswersIcon,
        events: EventsIcon,
        blocks: BlocksIcon
      }
    },

    summaryText() {
      const answerCount = this.answers.length
      const eventCount = this.events.length
      const blockCount = this.blocks.length
      const parts = []

      if (answerCount) parts.push(answerCount + ' answer' + (answerCount !== 1 ? 's' : ''))
      if (eventCount) parts.push(eventCount + ' event' + (eventCount !== 1 ? 's' : ''))
      if (blockCount) parts.push(blockCount + ' block' + (blockCount !== 1 ? 's' : ''))

      if (!parts.length) return 'This variable is not used in any event, answer or block.'

      let usageString = `<strong>${this.variableName}</strong> variable is used in `
      if (parts.length > 1) {
        usageString += parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1]
      } else {
        usageString += parts[0]
      }

      return usageString + '.'
    }
  },

  methods: {
    isEmpty,

    async fetchVariableRefs() {
      try {
        this.isLoading = true
        const { templates, answers, events } = await variablesService.getReferences(this.variableId)
        this.blocks = templates
        this.answers = answers
        this.events = events
      } catch (err) {
        toaster.error(err.message)
      } finally {
        this.isLoading = false
      }
    },

    getRefItems(itemType) {
      return this[itemType]
    },

    getBotName(botId) {
      return this.botList.find(bot => bot.id === botId)?.name || botId
    },

    // A bot+market variable carries its own market-variant bot id (variable.bot_id), but a
    // reference is recorded under the group's primary bot (variables.md §5). Open usages in the
    // variant where the variable actually applies; fall back to the recorded bot for
    // global/multibot scopes (no own bot_id) and standalone bots (variant id === recorded id).
    linkBotId(ref) {
      return this.viewedVariable?.bot_id || ref.bot_id
    },

    itemRoute(ref, itemType) {
      return `/bots/${this.linkBotId(ref)}/${itemType}/${ref.id}`
    }
  },

  mounted() {
    this.fetchVariableRefs()
  }
}
</script>

<style lang="scss" scoped>
.variable-usages-list {
  height: calc(100% - 100px);
}
</style>
